import { getAccessToken, getRefreshToken, storeTokens, clearAuthData } from "@/services/auth";
import { API_BASE_URL } from "@/config";

const API_BASE = API_BASE_URL;

/**
 * Try to refresh the access token
 */
async function tryRefreshToken(): Promise<boolean> {
	const refreshToken = getRefreshToken();
	if (!refreshToken) return false;

	try {
		const response = await fetch(`${API_BASE}/admin/api/auth/refresh-token`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refreshToken }),
		});

		if (!response.ok) return false;

		const data = await response.json();
		if (data.success && data.data) {
			storeTokens(data.data.accessToken, data.data.refreshToken);
			return true;
		}
		return false;
	} catch {
		return false;
	}
}

// Helper for simulation API calls
async function fetchSimulationApi<T>(
	endpoint: string,
	options?: RequestInit,
): Promise<T> {
	const token = getAccessToken();

	const response = await fetch(`${API_BASE}/admin/api/simulate${endpoint}`, {
		...options,
		headers: {
			"Content-Type": "application/json",
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...options?.headers,
		},
	});

	// Handle 401 - try to refresh token
	if (response.status === 401) {
		const refreshed = await tryRefreshToken();
		if (refreshed) {
			const newToken = getAccessToken();
			const retryResponse = await fetch(
				`${API_BASE}/admin/api/simulate${endpoint}`,
				{
					...options,
					headers: {
						"Content-Type": "application/json",
						...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
						...options?.headers,
					},
				},
			);

			if (!retryResponse.ok) {
				const error = await retryResponse.json().catch(() => ({}));
				throw new Error(error.message || `HTTP ${retryResponse.status}`);
			}

			const data = await retryResponse.json();
			if (data.success === false) {
				throw new Error(data.message || "API request failed");
			}

			return data.data ?? data;
		}
		clearAuthData();
		window.location.href = "/login";
		throw new Error("Session expired. Please login again.");
	}

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.message || `HTTP ${response.status}`);
	}

	const data = await response.json();
	if (data.success === false) {
		throw new Error(data.message || "API request failed");
	}

	return data.data ?? data;
}

// ==========================================
// Scoring Test
// ==========================================

export interface ScoringTestInput {
	processName: string;
	windowTitle: string;
	coveragePercent: number;
	zOrder: number;
	isActive: boolean;
	sessionDuration?: number;
	switchCount?: number;
	breaksToday?: number;
	consecutiveHours?: number;
	organizationId?: string;
}

export interface ScoringBreakdown {
	appScore: { value: number; weight: number; contribution: number; details: any };
	focusScore: { value: number; weight: number; contribution: number; details: any };
	contextScore: { value: number; weight: number; contribution: number; details: any };
	timePatternScore: { value: number; weight: number; contribution: number };
	balanceScore: { value: number; weight: number; contribution: number };
}

export interface ScoringResult {
	finalScore: number;
	label: string;
	emoji: string;
	message: string;
	color: string;
	breakdown: ScoringBreakdown;
	recommendations: string[];
	intensityMultiplier: number;
}

export async function testScoring(input: ScoringTestInput): Promise<ScoringResult> {
	return fetchSimulationApi("/scoring", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

// ==========================================
// KPI Test
// ==========================================

export interface KPITestInput {
	averageProductivityScore: number;
	averageActiveTimePercent: number;
	productiveAppTimePercent: number;
	attendancePercentage: number;
	punctualityPercentage: number;
	totalWorkedHours: number;
	expectedHours: number;
	isFieldWorker?: boolean;
}

export interface KPITestResult {
	finalScore: number;
	performanceLevel: string;
	weightedScores: {
		productivity?: { rawScore: number; weight: number; weightedScore: number };
		attendance: { rawScore: number; weight: number; weightedScore: number };
		output: { rawScore: number; weight: number; weightedScore: number };
	};
	thresholds: {
		exceptional: number;
		high: number;
		average: number;
		below: number;
	};
}

export async function testKPI(input: KPITestInput): Promise<KPITestResult> {
	return fetchSimulationApi("/kpi", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

// ==========================================
// Gamification Test
// ==========================================

export interface GamificationTestInput {
	currentStreak: number;
	productivityScore: number;
	focusHours: number;
	deepFocusSessions: number;
	totalXP: number;
	level: number;
}

export interface GamificationTestResult {
	xpEarned: number;
	newTotalXP: number;
	newLevel: number;
	leveledUp: boolean;
	streakUpdate: {
		active: boolean;
		newStreak: number;
	};
	breakdown: {
		baseXP: number;
		deepFocusBonus: number;
		streakMultiplier: number;
		productivityBonus: number;
	};
}

export async function testGamification(
	input: GamificationTestInput,
): Promise<GamificationTestResult> {
	return fetchSimulationApi("/gamification", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

// ==========================================
// URL Detection Test
// ==========================================

export interface URLDetectionInput {
	windowTitle: string;
	appName: string;
}

export interface URLClassification {
	category: string;
	isProductive: boolean;
	confidence: number;
	reasoning?: string;
	source: string;
}

export interface URLDetectionResult {
	detected: boolean;
	url: string | null;
	domain: string | null;
	confidence: number;
	source: string | null;
	classification: URLClassification | null;
	productivityLevel: "productive" | "neutral" | "unproductive";
}

export async function testURLDetection(
	input: URLDetectionInput,
): Promise<URLDetectionResult> {
	return fetchSimulationApi("/url-detection", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

// ==========================================
// App Classification Test
// ==========================================

export interface AppClassificationInput {
	appName: string;
	processName?: string;
	organizationId: string;
}

export interface AppClassificationResult {
	appName: string;
	classification: "productive" | "unproductive" | "neutral" | "pending_review";
	baseScore: number;
	contextRules: Array<{
		pattern: string;
		description: string;
		scoreAdjust: number;
	}>;
	isSystemDefault: boolean;
	found: boolean;
}

export async function testAppClassification(
	input: AppClassificationInput,
): Promise<AppClassificationResult> {
	return fetchSimulationApi("/app-classification", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

// ==========================================
// Full Simulation
// ==========================================

export interface FullSimulationInput {
	organizationId: string;
	userId?: string;
	days: number;
	scenario:
		| "productive-developer"
		| "distracted-day"
		| "designer-workflow"
		| "meeting-heavy";
	dryRun?: boolean;
}

export interface FullSimulationResult {
	sessionsCreated: number;
	screenshotsCreated: number;
	appUsageCreated: number;
	idleEventsCreated: number;
	summariesGenerated: number;
	gamificationUpdates: number;
	kpiJobsQueued: number;
	averageScore: number;
	errors: string[];
}

export async function runFullSimulation(
	input: FullSimulationInput,
): Promise<FullSimulationResult> {
	return fetchSimulationApi("/full-pipeline", {
		method: "POST",
		body: JSON.stringify(input),
	});
}

// ==========================================
// Cleanup
// ==========================================

export interface CleanupInput {
	organizationId: string;
	userId?: string;
	startDate: string;
	endDate: string;
	markedOnly?: boolean;
}

export interface CleanupResult {
	sessionsDeleted: number;
	screenshotsDeleted: number;
	appUsageDeleted: number;
	idleEventsDeleted: number;
	summariesDeleted: number;
	kpiDeleted: number;
}

export async function cleanupSimulationData(
	input: CleanupInput,
): Promise<CleanupResult> {
	const params = new URLSearchParams({
		organizationId: input.organizationId,
		startDate: input.startDate,
		endDate: input.endDate,
	});
	if (input.userId) params.append("userId", input.userId);
	if (input.markedOnly !== undefined)
		params.append("markedOnly", String(input.markedOnly));

	return fetchSimulationApi(`/cleanup?${params.toString()}`, {
		method: "DELETE",
	});
}

export async function previewCleanup(
	input: CleanupInput,
): Promise<CleanupResult & { preview: true }> {
	const params = new URLSearchParams({
		organizationId: input.organizationId,
		startDate: input.startDate,
		endDate: input.endDate,
		preview: "true",
	});
	if (input.userId) params.append("userId", input.userId);
	if (input.markedOnly !== undefined)
		params.append("markedOnly", String(input.markedOnly));

	return fetchSimulationApi(`/cleanup?${params.toString()}`, {
		method: "DELETE",
	});
}

// ==========================================
// Scenarios
// ==========================================

export interface SimulationScenario {
	id: string;
	name: string;
	description: string;
}

export async function getSimulationScenarios(): Promise<SimulationScenario[]> {
	return fetchSimulationApi("/scenarios", {
		method: "GET",
	});
}

// ==========================================
// User Profiles (for realistic simulation)
// ==========================================

export type UserProfileType =
	| "senior-developer"
	| "junior-developer"
	| "designer"
	| "manager"
	| "support"
	| "mixed";

export interface UserProfile {
	id: UserProfileType;
	name: string;
	description: string;
	productivityRange: [number, number];
	baseScenario: string;
}

export async function getUserProfiles(): Promise<UserProfile[]> {
	return fetchSimulationApi("/profiles", {
		method: "GET",
	});
}

// ==========================================
// Realistic Simulation
// ==========================================

export interface RealisticSimulationInput {
	organizationId: string;
	userIds?: string[];
	duration: "1-week" | "2-weeks" | "1-month" | "custom";
	customDays?: number;
	startDate?: string;
	teamComposition?: "auto" | "custom";
	customProfiles?: Record<string, UserProfileType>;
	includeWeekends?: boolean;
	dryRun?: boolean;
}

export interface UserBreakdown {
	userId: string;
	email: string;
	profile: UserProfileType;
	sessionsCreated: number;
	averageProductivity: number;
}

export interface RealisticSimulationResult extends FullSimulationResult {
	duration: string;
	workingDays: number;
	usersSimulated: number;
	userBreakdown: UserBreakdown[];
	dateRange: {
		start: string;
		end: string;
	};
}

export async function runRealisticSimulation(
	input: RealisticSimulationInput,
): Promise<RealisticSimulationResult> {
	return fetchSimulationApi("/realistic", {
		method: "POST",
		body: JSON.stringify(input),
	});
}
